import React, { useState, useEffect, useCallback } from 'react';
import { supabaseAdmin as supabase, supabaseAdmin, supabase as supabasePublic, listQuestionsAdmins, addQuestionsAdmin, removeQuestionsAdmin } from './supabaseClient';
import { LogoIcon } from './Logo';
import { useLanguage } from './LanguageContext';

const TABS = [
  { id: 'dashboard',  labelKey: 'admin_dashboard',  icon: '📊' },
  { id: 'analytics',  labelKey: 'admin_visitors',   icon: '📈' },
  { id: 'users',      labelKey: 'admin_users',       icon: '👥' },
  { id: 'payments',   labelKey: 'admin_paid',        icon: '💰' },
  { id: 'questions',  labelKey: 'admin_questions',   icon: '🔍' },
  { id: 'hidden',     labelKey: 'admin_hidden',      icon: '🚫' },
  { id: 'reports',    labelKey: 'admin_reports',     icon: '🚩' },
  { id: 'reviews',    labelKey: 'admin_opinions',    icon: '⭐' },
  { id: 'telegram',   labelKey: 'admin_telegram',    icon: '🤖' },
];

const S = {
  wrap: {
    minHeight: '100vh',
    background: 'var(--bg-page)',
    fontFamily: "'Tajawal',sans-serif", direction: 'rtl', color: 'var(--text)',
  },
  header: {
    background: 'var(--bg-header)', borderBottom: '1px solid var(--border-header)',
    padding: '14px 24px', display: 'flex', alignItems: 'center',
    justifyContent: 'space-between',
  },
  tab: (active) => ({
    padding: '10px 18px', borderRadius: 10, border: 'none', cursor: 'pointer',
    fontFamily: "'Tajawal',sans-serif", fontSize: 14, fontWeight: active ? 700 : 400,
    background: active ? 'rgba(124,58,237,0.18)' : 'var(--bg-card-alt)',
    color: active ? '#a855f7' : 'var(--text-secondary)',
    borderBottom: active ? '2px solid #a855f7' : '2px solid transparent',
    transition: 'all 0.2s',
  }),
  card: {
    background: 'var(--bg-card)', borderRadius: 16,
    border: '1px solid var(--border)', padding: 20,
  },
  statCard: {
    background: 'var(--bg-card)', borderRadius: 16,
    border: '1px solid var(--border)', padding: '20px 24px',
    textAlign: 'center', flex: 1,
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    textAlign: 'right', padding: '10px 14px', fontSize: 13,
    color: 'var(--text-secondary)', borderBottom: '1px solid var(--border)',
  },
  td: {
    padding: '12px 14px', fontSize: 13, borderBottom: '1px solid var(--border)',
    color: 'var(--text)',
  },
  input: {
    width: '100%', padding: '12px 16px', borderRadius: 10, boxSizing: 'border-box',
    background: 'var(--bg-input)', border: '1px solid var(--border)',
    color: 'var(--text)', fontFamily: "'Tajawal',sans-serif", fontSize: 14, outline: 'none',
  },
  btn: (color='#7c3aed') => ({
    padding: '6px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
    background: color, color: '#fff', fontSize: 12,
    fontFamily: "'Tajawal',sans-serif", fontWeight: 600,
  }),
  badge: (color) => ({
    padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
    background: color, color: '#fff', display: 'inline-block',
  }),
};

// ─── Analytics ───────────────────────────────────────────
const FLAG_NAMES = {
  SA: 'السعودية', AE: 'الإمارات', KW: 'الكويت', BH: 'البحرين', QA: 'قطر',
  OM: 'عُمان', JO: 'الأردن', EG: 'مصر', LB: 'لبنان', IQ: 'العراق',
  YE: 'اليمن', SY: 'سوريا', MA: 'المغرب', DZ: 'الجزائر', TN: 'تونس',
  LY: 'ليبيا', US: 'أمريكا', GB: 'بريطانيا', DE: 'ألمانيا', FR: 'فرنسا',
  TR: 'تركيا', PK: 'باكستان', IN: 'الهند', CA: 'كندا', AU: 'أستراليا',
  Unknown: 'غير محدد',
};

const toFlag = (code) => {
  if (!code || code === 'Unknown') return '🌍';
  return [...code.toUpperCase()].map(c =>
    String.fromCodePoint(0x1F1E6 - 65 + c.charCodeAt(0))
  ).join('');
};

function Analytics() {
  const [data, setData] = useState(null);
  const [loadErr, setLoadErr] = useState(null);

  useEffect(() => {
    const load = async () => {
      const since = new Date(Date.now() - 30 * 86400000).toISOString();
      const { data: rows, error } = await supabase
        .from('page_views')
        .select('visited_at, country_code, session_id')
        .gte('visited_at', since)
        .order('visited_at', { ascending: false })
        .limit(5000);

      if (error) { setLoadErr(error.message); return; }
      const safeRows = rows || [];

      const todayStr = new Date().toDateString();
      const yestStr  = new Date(Date.now() - 86400000).toDateString();

      const todayCount = safeRows.filter(r => new Date(r.visited_at).toDateString() === todayStr).length;
      const yestCount  = safeRows.filter(r => new Date(r.visited_at).toDateString() === yestStr).length;
      const unique     = new Set(safeRows.map(r => r.session_id)).size;

      const countryCounts = {};
      safeRows.forEach(r => {
        const c = r.country_code || 'Unknown';
        countryCounts[c] = (countryCounts[c] || 0) + 1;
      });
      const countries = Object.entries(countryCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 15);

      const daily = {};
      for (let i = 13; i >= 0; i--) {
        const d = new Date(Date.now() - i * 86400000);
        daily[d.toISOString().slice(0, 10)] = 0;
      }
      safeRows.forEach(r => {
        const k = (r.visited_at || '').slice(0, 10);
        if (k in daily) daily[k]++;
      });

      setData({
        total: safeRows.length,
        unique,
        today: todayCount,
        yesterday: yestCount,
        countries,
        daily: Object.entries(daily),
      });
    };
    load();
  }, []);

  if (loadErr) return <p style={{ color: '#ef4444', padding: 20 }}>⚠️ خطأ: {loadErr}</p>;
  if (!data)   return <p style={{ color: 'var(--text-muted)', padding: 20 }}>جاري التحميل...</p>;

  const maxBar = Math.max(...data.daily.map(d => d[1]), 1);

  const summaryCards = [
    { label: 'إجمالي الزيارات',  value: data.total.toLocaleString(),  icon: '👁️', color: '#3b82f6' },
    { label: 'زوار فريدون',       value: data.unique.toLocaleString(), icon: '👤', color: '#8b5cf6' },
    { label: 'زيارات اليوم',      value: data.today,                  icon: '📅', color: '#10b981' },
    { label: 'زيارات أمس',        value: data.yesterday,              icon: '📆', color: '#f59e0b' },
    { label: 'دول مختلفة',        value: data.countries.length,       icon: '🌍', color: '#ef4444' },
  ];

  return (
    <div>
      <h2 style={{ marginBottom: 24, color: '#f0b429' }}>📈 إحصائيات الزوار (آخر 30 يوم)</h2>

      {/* بطاقات الملخص */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginBottom: 28 }}>
        {summaryCards.map(c => (
          <div key={c.label} style={{ ...S.statCard, borderTop: `3px solid ${c.color}`, minWidth: 110 }}>
            <div style={{ fontSize: 28, marginBottom: 6 }}>{c.icon}</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: c.color }}>{c.value}</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>{c.label}</div>
          </div>
        ))}
      </div>

      {/* رسم بياني يومي */}
      <div style={{ ...S.card, marginBottom: 24 }}>
        <h3 style={{ margin: '0 0 20px', fontSize: 15, color: 'var(--text)' }}>📊 الزيارات اليومية — آخر 14 يوم</h3>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 5, height: 140, padding: '0 4px' }}>
          {data.daily.map(([date, count]) => {
            const isToday = date === new Date().toISOString().slice(0, 10);
            const barH = count > 0 ? Math.max((count / maxBar) * 90, 6) : 3;
            return (
              <div key={date} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <div style={{ fontSize: 10, color: '#a855f7', fontWeight: 700, minHeight: 14 }}>
                  {count > 0 ? count : ''}
                </div>
                <div style={{
                  width: '100%', borderRadius: '5px 5px 0 0',
                  background: isToday ? '#10b981' : count > 0 ? '#7c3aed' : 'var(--border)',
                  height: barH, transition: 'height 0.4s ease',
                  boxShadow: isToday ? '0 0 8px rgba(16,185,129,0.4)' : count > 0 ? '0 0 6px rgba(124,58,237,0.3)' : 'none',
                }} />
                <div style={{
                  fontSize: 9, color: 'var(--text-muted)',
                  transform: 'rotate(-45deg)', transformOrigin: 'center',
                  whiteSpace: 'nowrap', marginTop: 2,
                }}>
                  {date.slice(5)}
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', gap: 16, marginTop: 18, fontSize: 11, color: 'var(--text-muted)' }}>
          <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#7c3aed', marginLeft: 4 }} />يوم عادي</span>
          <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#10b981', marginLeft: 4 }} />اليوم</span>
        </div>
      </div>

      {/* جدول الدول */}
      <div style={S.card}>
        <h3 style={{ margin: '0 0 16px', fontSize: 15, color: 'var(--text)' }}>🌍 الزوار حسب الدولة</h3>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>الدولة</th>
              <th style={{ ...S.th, textAlign: 'center' }}>الزيارات</th>
              <th style={{ ...S.th, textAlign: 'center' }}>النسبة</th>
              <th style={{ ...S.th, minWidth: 120 }}>التوزيع</th>
            </tr>
          </thead>
          <tbody>
            {data.countries.map(([code, count]) => {
              const pct = Math.round((count / data.total) * 100);
              return (
                <tr key={code}>
                  <td style={S.td}>
                    <span style={{ fontSize: 22, marginLeft: 8 }}>{toFlag(code)}</span>
                    <span style={{ fontWeight: 600 }}>{FLAG_NAMES[code] || code}</span>
                  </td>
                  <td style={{ ...S.td, textAlign: 'center', fontWeight: 800, color: '#7c3aed', fontSize: 15 }}>
                    {count.toLocaleString()}
                  </td>
                  <td style={{ ...S.td, textAlign: 'center', color: 'var(--text-secondary)', fontWeight: 600 }}>
                    {pct}%
                  </td>
                  <td style={{ ...S.td }}>
                    <div style={{ background: 'var(--border)', borderRadius: 6, height: 8, overflow: 'hidden' }}>
                      <div style={{
                        background: 'linear-gradient(90deg, #7c3aed, #a855f7)',
                        height: '100%', width: `${pct}%`, borderRadius: 6,
                        transition: 'width 0.5s ease',
                      }} />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Dashboard ───────────────────────────────────────────
function Dashboard({ adminPhone }) {
  const [stats, setStats] = useState(null);
  const [questionsBySubject, setQuestionsBySubject] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const { data: { session } } = await supabasePublic.auth.getSession();
        const res = await fetch('/api/admin-stats', {
          headers: { Authorization: `Bearer ${session?.access_token || ''}` },
          cache: 'no-store',
        });
        const data = await res.json();
        if (data.ok) {
          setStats({
            users: data.users || 0,
            subs: data.subs || 0,
            sessions: data.sessions || 0,
            questions: data.questions || 0,
            revenue: data.revenue || 0,
          });
        }
      } catch (e) {
        console.error('dashboard error', e);
      }

      // إحصائيات الأسئلة حسب القسم — نجلب كل قسم بشكل منفصل
      const TAHSILI = ['رياضيات','فيزياء','كيمياء','أحياء'];
      const ALL_SUBJECTS = ['كمي','لفظي',...TAHSILI];
      const counts = {};
      await Promise.all(ALL_SUBJECTS.map(async (s) => {
        const { count } = await supabase
          .from('questions')
          .select('id', { count: 'exact', head: true })
          .eq('is_active', true)
          .eq('subject', s);
        counts[s] = count || 0;
      }));

      const result = [];
      ['كمي','لفظي'].forEach(s => {
        if (counts[s]) result.push({ label: s, count: counts[s], indent: false });
      });
      const tahsiliTotal = TAHSILI.reduce((sum, s) => sum + (counts[s] || 0), 0);
      if (tahsiliTotal > 0) {
        result.push({ label: 'تحصيلي', count: tahsiliTotal, indent: false, isParent: true });
        TAHSILI.forEach(s => {
          if (counts[s]) result.push({ label: s, count: counts[s], indent: true });
        });
      }
      setQuestionsBySubject(result);
    };
    load();
  }, [adminPhone]); // eslint-disable-line react-hooks/exhaustive-deps

  const cards = stats ? [
    { label: 'إجمالي المستخدمين', value: stats.users, icon: '👥', color: '#3b82f6' },
    { label: 'المشتركون النشطون', value: stats.subs, icon: '✅', color: '#10b981' },
    { label: 'جلسات اليوم', value: stats.sessions, icon: '🎯', color: '#f59e0b' },
    { label: 'الأسئلة النشطة', value: stats.questions.toLocaleString(), icon: '📝', color: '#7c6df8' },
    { label: 'الإيرادات الكلية', value: `${stats.revenue.toFixed(0)} ر.س`, icon: '💰', color: '#ef4444' },
  ] : [];

  return (
    <div>
      <h2 style={{ marginBottom: 24, color: '#f0b429' }}>📊 لوحة المعلومات</h2>
      {!stats ? (
        <p style={{ color: 'var(--text-muted)' }}>جاري التحميل...</p>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
          {cards.map(c => (
            <div key={c.label} style={{ ...S.statCard, borderTop: `3px solid ${c.color}` }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>{c.icon}</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: c.color }}>{c.value}</div>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>{c.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* إحصائيات الأسئلة حسب القسم */}
      {questionsBySubject.length > 0 && (
        <div style={{ ...S.card, marginTop: 32 }}>
          <h3 style={{ color: '#f0b429', marginBottom: 16, fontSize: 16 }}>📚 الأسئلة النشطة حسب القسم</h3>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>القسم</th>
                <th style={{ ...S.th, textAlign: 'left' }}>عدد الأسئلة</th>
                <th style={{ ...S.th, textAlign: 'left' }}>النسبة</th>
              </tr>
            </thead>
            <tbody>
              {questionsBySubject.map((row, i) => {
                const total = questionsBySubject.filter(r => !r.indent).reduce((s, r) => s + r.count, 0);
                const pct = ((row.count / total) * 100).toFixed(1);
                return (
                  <tr key={i} style={{ background: row.isParent ? 'rgba(124,109,248,0.08)' : 'transparent' }}>
                    <td style={{ ...S.td, paddingRight: row.indent ? 32 : 14, color: row.isParent ? '#a855f7' : row.indent ? 'var(--text-secondary)' : 'var(--text)', fontWeight: row.isParent ? 700 : 400 }}>
                      {row.indent ? '↳ ' : ''}{row.label}
                    </td>
                    <td style={{ ...S.td, textAlign: 'left', fontWeight: 700, color: row.isParent ? '#a855f7' : '#7c6df8' }}>{row.count.toLocaleString()}</td>
                    <td style={{ ...S.td, textAlign: 'left' }}>
                      {!row.indent && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ flex: 1, height: 6, background: 'var(--border)', borderRadius: 3 }}>
                            <div style={{ width: `${pct}%`, height: '100%', background: row.isParent ? '#a855f7' : '#7c6df8', borderRadius: 3 }} />
                          </div>
                          <span style={{ color: 'var(--text-muted)', fontSize: 12, minWidth: 40 }}>{pct}%</span>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Users ───────────────────────────────────────────────
function GrantSubscription({ onGranted }) {
  const [phone, setPhone] = useState('');
  const [planId, setPlanId] = useState('');
  const [months, setMonths] = useState(1);
  const [plans, setPlans] = useState([]);
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    supabase.from('plans').select('id, name_ar').then(({ data }) => setPlans(data || []));
  }, []);

  const grant = async () => {
    if (!phone || !planId) { setMsg('❌ أدخل رقم الجوال والباقة'); return; }
    setLoading(true); setMsg('');
    const formatted = phone.startsWith('+966') ? phone : '+966' + phone.replace(/^0/, '');
    let { data: userData } = await supabase.from('users').select('id').ilike('phone', `%${phone.replace(/^0/, '')}%`).maybeSingle();

    // إنشاء المستخدم تلقائياً إذا ما موجود
    if (!userData) {
      const newId = crypto.randomUUID();
      const { data: newUser, error: userError } = await supabase
        .from('users')
        .insert({ id: newId, phone: formatted, full_name: '', stage: 'home' })
        .select('id')
        .single();
      if (userError) { setMsg('❌ فشل إنشاء المستخدم: ' + userError.message); setLoading(false); return; }
      userData = newUser;
    }

    const expiresAt = new Date();
    expiresAt.setMonth(expiresAt.getMonth() + Number(months));
    const { error } = await supabase.from('subscriptions').insert({
      user_id: userData.id,
      plan_id: planId,
      status: 'active',
      start_date: new Date().toISOString(),
      expires_at: expiresAt.toISOString(),
    });
    if (error) { setMsg('❌ خطأ: ' + error.message); }
    else { setMsg(`✅ تم منح الاشتراك لـ ${formatted} لمدة ${months} شهر`); setPhone(''); onGranted?.(); }
    setLoading(false);
  };

  return (
    <div style={{ ...S.card, marginBottom: 24, borderColor: '#7c6df8' }}>
      <h3 style={{ color: '#a855f7', marginBottom: 16, fontSize: 15 }}>🎁 منح اشتراك يدوي</h3>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <input style={{ ...S.input, flex: 2, minWidth: 160 }} placeholder="رقم الجوال (05xxxxxxxx)" value={phone} onChange={e => setPhone(e.target.value)} />
        <select style={{ ...S.input, flex: 1, minWidth: 120 }} value={planId} onChange={e => setPlanId(e.target.value)}>
          <option value="">اختر الباقة</option>
          {plans.map(p => <option key={p.id} value={p.id}>{p.name_ar}</option>)}
        </select>
        <select style={{ ...S.input, flex: 1, minWidth: 100 }} value={months} onChange={e => setMonths(e.target.value)}>
          {[1,2,3,6,12].map(m => <option key={m} value={m}>{m === 12 ? 'سنة' : `${m} شهر`}</option>)}
        </select>
        <button onClick={grant} disabled={loading} style={{ ...S.btn('#7c6df8'), padding: '12px 20px', fontSize: 14 }}>
          {loading ? '...' : '✅ منح'}
        </button>
      </div>
      {msg && <p style={{ marginTop: 10, fontSize: 13, color: msg.startsWith('✅') ? '#86efac' : '#fca5a5' }}>{msg}</p>}
    </div>
  );
}

// ─── صلاحية رفع الأسئلة فقط ─────────────────────────────
function QuestionsAdminManager() {
  const [admins, setAdmins] = useState([]);
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');

  const load = async () => setAdmins(await listQuestionsAdmins());
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!phone) { setMsg('❌ أدخل رقم الجوال'); return; }
    setLoading(true); setMsg('');
    const { error } = await addQuestionsAdmin(phone);
    if (error) setMsg('❌ خطأ: ' + error.message);
    else { setMsg('✅ تم منح صلاحية رفع الأسئلة'); setPhone(''); await load(); }
    setLoading(false);
  };

  const remove = async (p) => {
    if (!window.confirm(`إزالة صلاحية رفع الأسئلة من ${p}؟`)) return;
    await removeQuestionsAdmin(p);
    load();
  };

  return (
    <div style={{ ...S.card, marginBottom: 24, borderColor: '#3b82f6' }}>
      <h3 style={{ color: '#3b82f6', marginBottom: 16, fontSize: 15 }}>🔍 صلاحية رفع الأسئلة فقط</h3>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <input style={{ ...S.input, flex: 2, minWidth: 160 }} placeholder="رقم الجوال (05xxxxxxxx)" value={phone} onChange={e => setPhone(e.target.value)} />
        <button onClick={add} disabled={loading} style={{ ...S.btn('#3b82f6'), padding: '12px 20px', fontSize: 14 }}>
          {loading ? '...' : '✅ منح'}
        </button>
      </div>
      {msg && <p style={{ marginTop: 10, fontSize: 13, color: msg.startsWith('✅') ? '#86efac' : '#fca5a5' }}>{msg}</p>}
      {admins.length > 0 && (
        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {admins.map(a => (
            <div key={a.phone} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13 }}>
              <span>{a.phone}</span>
              <button onClick={() => remove(a.phone)} style={{ ...S.btn('#ef4444'), fontSize: 11 }}>إزالة</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active'); // active | expired | cancelled | all
  const [neverOnly, setNeverOnly] = useState(false);          // نشطون لم يدخلوا الموقع أبداً
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      // جلب كل الاشتراكات بلا فلترة على الحالة — الفلترة تتم في الواجهة
      const { data: subs } = await supabase
        .from('subscriptions')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(200);
      if (!subs?.length) { setUsers([]); setLoading(false); return; }

      const userIds = [...new Set(subs.map(s => s.user_id).filter(Boolean))];
      const planIds = [...new Set(subs.map(s => s.plan_id).filter(Boolean))];

      const [{ data: usersData }, { data: plansData }, { data: sessionsData }] = await Promise.all([
        userIds.length ? supabase.from('users').select('id, phone').in('id', userIds) : { data: [] },
        planIds.length ? supabase.from('plans').select('id, name_ar').in('id', planIds) : { data: [] },
        userIds.length ? supabasePublic.from('auth_sessions').select('user_id, last_active, is_active').in('user_id', userIds) : { data: [] },
      ]);

      const usersMap = {};
      (usersData || []).forEach(u => { usersMap[u.id] = u; });
      const plansMap = {};
      (plansData || []).forEach(p => { plansMap[p.id] = p; });

      // المستخدم قد يملك أكثر من صف جلسة — نأخذ الأحدث دائماً
      const activityMap = {};
      (sessionsData || []).forEach(a => {
        if (!a.last_active) return;
        const prev = activityMap[a.user_id];
        if (!prev || new Date(a.last_active) > new Date(prev.last_active)) {
          activityMap[a.user_id] = { last_active: a.last_active, is_active: a.is_active };
        }
      });

      const merged = subs.map(s => ({
        ...s,
        users: usersMap[s.user_id] || null,
        plans: plansMap[s.plan_id] || null,
        lastActive: activityMap[s.user_id]?.last_active || null,
        sessionOpen: activityMap[s.user_id]?.is_active || false,
      }));
      setUsers(merged);
      setLoading(false);
    };
    load();
  }, [refresh]); // eslint-disable-line react-hooks/exhaustive-deps

  // تصنيف الحالة الفعلية للاشتراك
  const subState = (u) => {
    if (u.status === 'cancelled') return 'cancelled';
    if (new Date(u.expires_at) <= Date.now()) return 'expired';
    return 'active';
  };

  const counts = {
    all:       users.length,
    active:    users.filter(u => subState(u) === 'active').length,
    expired:   users.filter(u => subState(u) === 'expired').length,
    cancelled: users.filter(u => subState(u) === 'cancelled').length,
  };

  const FILTERS = [
    { id: 'active',    label: '✅ نشط',    color: '#10b981' },
    { id: 'expired',   label: '⏰ منتهي',  color: '#ef4444' },
    { id: 'cancelled', label: '🚫 ملغي',   color: '#64748b' },
    { id: 'all',       label: 'الكل',      color: '#7c3aed' },
  ];

  // مشتركون فعّالون لم يسجّلوا دخولاً أبداً — الأقرب انتهاءً أولاً
  const neverLoggedIn = users
    .filter(u => subState(u) === 'active' && !u.lastActive)
    .sort((a, b) => new Date(a.expires_at) - new Date(b.expires_at));

  const filtered = (neverOnly ? neverLoggedIn : users
    .filter(u => statusFilter === 'all' || subState(u) === statusFilter))
    .filter(u => (u.users?.phone || '').includes(search));

  const planColor = (type) => {
    if (!type) return 'var(--text-secondary)';
    if (type.includes('elite') || type.includes('شامل')) return '#f59e0b';
    if (type.includes('pro') || type.includes('متقدم')) return '#7c6df8';
    return '#3b82f6';
  };

  const isOnline = (u) =>
    u.sessionOpen && u.lastActive && (Date.now() - new Date(u.lastActive).getTime()) < 2 * 60000;

  return (
    <div>
      <h2 style={{ marginBottom: 20, color: '#f0b429' }}>👥 المستخدمون المشتركون</h2>
      <GrantSubscription onGranted={() => setRefresh(r => r + 1)} />
      <QuestionsAdminManager />

      {/* تنبيه: مشتركون فعّالون لم يدخلوا الموقع أبداً */}
      {!loading && neverLoggedIn.length > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 14,
          background: 'rgba(240,180,40,0.06)', border: '1px solid rgba(240,180,40,0.3)',
          borderRadius: 12, padding: '12px 16px',
        }}>
          <span style={{ color: '#f0b429', fontWeight: 700, fontSize: 13 }}>
            ⚠️ {neverLoggedIn.length} مشترك فعّال لم يدخلوا الموقع أبداً
          </span>
          <button onClick={() => setNeverOnly(v => !v)} style={{ ...S.btn(neverOnly ? '#7c3aed' : '#f59e0b'), fontSize: 12 }}>
            {neverOnly ? 'عرض كل الاشتراكات' : 'عرضهم (الأقرب انتهاءً أولاً)'}
          </button>
        </div>
      )}

      {/* فلاتر الحالة */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14, opacity: neverOnly ? 0.4 : 1 }}>
        {FILTERS.map(f => (
          <button key={f.id} onClick={() => setStatusFilter(f.id)} style={{
            ...S.btn(statusFilter === f.id ? f.color : 'var(--bg-card-alt)'),
            fontSize: 12,
            border: `1px solid ${statusFilter === f.id ? f.color : 'rgba(255,255,255,0.1)'}`,
          }}>
            {f.label} ({counts[f.id]})
          </button>
        ))}
      </div>

      <input
        style={{ ...S.input, marginBottom: 16 }}
        placeholder="🔍 ابحث برقم الجوال..."
        value={search}
        onChange={e => setSearch(e.target.value)}
      />
      <div style={S.card}>
        {loading ? <p style={{ color: 'var(--text-muted)' }}>جاري التحميل...</p> : (
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>رقم الجوال</th>
                <th style={S.th}>الباقة</th>
                <th style={S.th}>المدة</th>
                <th style={S.th}>تاريخ الاشتراك</th>
                <th style={S.th}>ينتهي في</th>
                <th style={S.th}>الحالة</th>
                <th style={S.th}>آخر دخول</th>
                <th style={S.th}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(u => {
                const exp = new Date(u.expires_at);
                const start = new Date(u.start_date || u.created_at);
                const daysLeft = Math.ceil((exp - Date.now()) / 86400000);
                const totalDays = Math.ceil((exp - start) / 86400000);
                const duration = totalDays >= 355 ? 'سنة' : totalDays >= 170 ? '6 شهور' : totalDays >= 85 ? '3 شهور' : totalDays >= 55 ? 'شهرين' : 'شهر';
                const st = subState(u);
                const online = isOnline(u);
                return (
                  <tr key={u.id} style={{ opacity: st === 'active' ? 1 : 0.65 }}>
                    <td style={S.td}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
                        {(u.users?.phone || '—').replace(/^\+/, '')}
                        <span title={online ? 'متصل الآن' : 'غير متصل'} style={{
                          width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                          background: online ? '#22c55e' : 'var(--border)',
                          boxShadow: online ? '0 0 6px #22c55e' : 'none',
                        }} />
                      </span>
                    </td>
                    <td style={S.td}>
                      <span style={S.badge(planColor(u.plans?.name_ar))}>
                        {u.plans?.name_ar || '—'}
                      </span>
                    </td>
                    <td style={S.td}>{duration}</td>
                    <td style={S.td}>{start.toLocaleDateString('ar-SA')}</td>
                    <td style={S.td}>{exp.toLocaleDateString('ar-SA')}</td>
                    <td style={S.td}>
                      {st === 'cancelled' ? (
                        <span style={S.badge('#64748b')}>🚫 ملغي</span>
                      ) : st === 'expired' ? (
                        <span style={S.badge('#ef4444')}>⏰ منتهي</span>
                      ) : (
                        <span style={S.badge(daysLeft > 7 ? '#10b981' : '#f59e0b')}>
                          {daysLeft} يوم
                        </span>
                      )}
                    </td>
                    <td style={S.td}>
                      {u.lastActive
                        ? (() => {
                            const d = new Date(u.lastActive);
                            const mins = Math.floor((Date.now() - d.getTime()) / 60000);
                            const hrs  = Math.floor(mins / 60);
                            const days = Math.floor(hrs / 24);
                            const months = Math.floor(days / 30);
                            const years  = Math.floor(days / 365);
                            if (online)      return <span style={{ color: '#22c55e', fontSize: 11, fontWeight: 700 }}>🟢 الآن</span>;
                            if (mins < 60)   return <span style={{ color: 'var(--text-secondary)', fontSize: 11 }}>منذ {mins} دقيقة</span>;
                            if (hrs < 24)    return <span style={{ color: 'var(--text-secondary)', fontSize: 11 }}>منذ {hrs} ساعة</span>;
                            if (days < 30)   return <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>منذ {days} يوم</span>;
                            if (months < 12) return <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>منذ {months} شهر</span>;
                            return <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>منذ {years} سنة</span>;
                          })()
                        : <span style={{ color: st === 'active' ? '#f59e0b' : 'var(--text-muted)', fontSize: 11, fontWeight: st === 'active' ? 700 : 400 }}>لم يدخل</span>
                      }
                    </td>
                    <td style={S.td}>
                      {st === 'active' && (
                        <button onClick={async () => {
                          if (!window.confirm('إلغاء اشتراك هذا المستخدم؟')) return;
                          await supabase.from('subscriptions').update({ status: 'cancelled' }).eq('id', u.id);
                          setRefresh(r => r + 1);
                        }} style={{ ...S.btn('#ef4444'), fontSize: 11 }}>إلغاء</button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {!filtered.length && (
                <tr><td colSpan={8} style={{ ...S.td, textAlign: 'center', color: 'var(--text-muted)' }}>لا يوجد نتائج</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
      <p style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 8 }}>
        يُعرض {filtered.length} من {counts.all} اشتراك
      </p>
    </div>
  );
}

// ─── Payments ────────────────────────────────────────────
function Payments() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState('');
  const [noMetaPayments, setNoMetaPayments] = useState([]);
  const [syncProblems, setSyncProblems] = useState([]);   // دفعات فشل تسجيلها مع السبب
  const [linkPhones, setLinkPhones] = useState({});   // moyasar_id → phone input
  const [linkStatus, setLinkStatus] = useState({});   // moyasar_id → 'linking'|'ok'|'error:msg'

  // دفعات في موياسر ليست اشتراكات (مثل فاتورة تمويل رصيد الاسترداد) — نخفيها من التنبيه. الحفظ في هذا المتصفح فقط
  const IGNORED_KEY = 'qadder_ignored_payments';
  const [ignoredIds, setIgnoredIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem(IGNORED_KEY) || '[]'); } catch { return []; }
  });
  const saveIgnored = (ids) => {
    setIgnoredIds(ids);
    try { localStorage.setItem(IGNORED_KEY, JSON.stringify(ids)); } catch { /* التخزين غير متاح */ }
  };
  const ignorePayment = (p) => {
    if (!window.confirm(`تجاهل دفعة ${p.amount} ر.س؟\nتخفى من التنبيه فقط، ولا تُسجَّل كاشتراك ولا تدخل الإيرادات.`)) return;
    saveIgnored([...new Set([...ignoredIds, p.id])]);
  };
  const visibleNoMeta = noMetaPayments.filter(p => !ignoredIds.includes(p.id));

  const load = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('payments')
      .select('*, plans(name_ar), users(phone)')
      .order('paid_at', { ascending: false })
      .limit(100);
    setPayments(data || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const syncFromMoyasar = async () => {
    setSyncing(true);
    setSyncMsg('');
    try {
        const { data: { session } } = await supabasePublic.auth.getSession();
      if (!session) {
        setSyncMsg('❌ الجلسة منتهية — سجّل دخول من جديد');
        setSyncing(false);
        return;
      }

      const res = await fetch('/api/sync-payments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (data.ok) {
        setSyncMsg(`✅ تمت المزامنة — أضيف: ${data.added}، تخطّي: ${data.skipped}${data.errors ? `، فشل: ${data.errors}` : ''}`);
        if (data.noMetadata?.length) setNoMetaPayments(data.noMetadata);
        setSyncProblems(data.problems || []);
        await load();
      } else {
        setSyncMsg(`❌ خطأ: ${data.error}`);
      }
    } catch (e) {
      setSyncMsg(`❌ خطأ: ${e.message}`);
    }
    setSyncing(false);
  };

  const total = payments.reduce((s, p) => s + (p.amount_halalas || 0), 0) / 100;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, color: '#f0b429' }}>💰 سجل المدفوعات</h2>
        <button onClick={syncFromMoyasar} disabled={syncing} style={{
          background: '#7c6df8', color: '#fff', border: 'none', borderRadius: 8,
          padding: '6px 16px', cursor: 'pointer', fontFamily: 'Tajawal, sans-serif', fontSize: 13,
        }}>
          {syncing ? '⏳ جاري المزامنة...' : '🔄 مزامنة من Moyasar'}
        </button>
        {syncMsg && <span style={{ fontSize: 13, color: syncMsg.startsWith('✅') ? '#10b981' : '#f87171' }}>{syncMsg}</span>}
        {ignoredIds.length > 0 && (
          <button onClick={() => saveIgnored([])} style={{
            background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer',
            fontFamily: 'Tajawal, sans-serif', fontSize: 12, textDecoration: 'underline',
          }}>
            إظهار المتجاهَلة ({ignoredIds.length})
          </button>
        )}
      </div>

      {syncProblems.length > 0 && (
        <div style={{
          marginBottom: 20, background: 'rgba(239,68,68,0.06)',
          border: '1px solid rgba(239,68,68,0.3)', borderRadius: 12, padding: '14px 16px',
        }}>
          <div style={{ color: '#f87171', fontWeight: 700, fontSize: 13, marginBottom: 10 }}>
            ❌ {syncProblems.length} دفعة مدفوعة في Moyasar تعذّر تسجيلها
          </div>
          {syncProblems.map(p => (
            <div key={p.id} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#a78bfa' }}>{p.id}</span>
              <span style={{ color: '#e2e8f0', fontSize: 12 }}>{p.reason}</span>
            </div>
          ))}
        </div>
      )}

      {visibleNoMeta.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ color: '#f0b429', fontWeight: 700, fontSize: 13, marginBottom: 10 }}>
            ⚠️ {visibleNoMeta.length} دفعة ناجحة بدون ربط بمستخدم
          </div>
          {visibleNoMeta.map(p => {
            const st = linkStatus[p.id];
            const done = st === 'ok';
            return (
              <div key={p.id} style={{
                background: done ? 'rgba(16,185,129,0.06)' : 'rgba(240,180,40,0.06)',
                border: `1px solid ${done ? 'rgba(16,185,129,0.25)' : 'rgba(240,180,40,0.25)'}`,
                borderRadius: 12, padding: '14px 16px', marginBottom: 10,
              }}>
                {/* معلومات الدفعة */}
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10, alignItems: 'center' }}>
                  <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#a78bfa' }}>{p.id}</span>
                  <span style={{ background: 'rgba(16,185,129,0.12)', color: '#10b981', borderRadius: 6, padding: '2px 8px', fontSize: 12, fontWeight: 700 }}>{p.amount} ر.س</span>
                  <span style={{ color: '#94a3b8', fontSize: 12 }}>{p.created_at?.slice(0, 10)}</span>
                  <span style={{ color: '#e2e8f0', fontSize: 12 }}>{p.description}</span>
                </div>

                {done ? (
                  <div style={{ color: '#10b981', fontSize: 13, fontWeight: 700 }}>✅ تم الربط وتفعيل الاشتراك</div>
                ) : (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <input
                      placeholder="رقم جوال صاحب الدفعة (05xxxxxxxx)"
                      value={linkPhones[p.id] || ''}
                      onChange={e => setLinkPhones(prev => ({ ...prev, [p.id]: e.target.value }))}
                      style={{
                        ...S.input, flex: 1, minWidth: 200, fontSize: 13, padding: '8px 12px',
                        borderColor: st?.startsWith('error') ? '#f87171' : undefined,
                      }}
                    />
                    <button
                      disabled={st === 'linking' || !linkPhones[p.id]}
                      onClick={async () => {
                        const rawPhone = linkPhones[p.id] || '';
                        const formatted = rawPhone.startsWith('+966') ? rawPhone : '+966' + rawPhone.replace(/^0/, '');
                        setLinkStatus(prev => ({ ...prev, [p.id]: 'linking' }));

                        // جلب أو إنشاء المستخدم
                        let { data: userData } = await supabaseAdmin
                          .from('users').select('id').ilike('phone', `%${rawPhone.replace(/^0/, '')}`).maybeSingle();
                        if (!userData) {
                          const { data: newUser } = await supabaseAdmin
                            .from('users').insert({ id: crypto.randomUUID(), phone: formatted, full_name: '', stage: 'home' }).select('id').single();
                          userData = newUser;
                        }
                        if (!userData) {
                          setLinkStatus(prev => ({ ...prev, [p.id]: 'error: فشل إنشاء المستخدم' }));
                          return;
                        }

                        // تحديد plan_id من الوصف
                        const desc = (p.description || '').toLowerCase();
                        let planId = desc.includes('شامل') || desc.includes('elite') ? 'elite'
                          : desc.includes('متقدم') || desc.includes('pro') ? 'pro' : 'basic';

                        // استخراج plan_id الحقيقي من جدول plans
                        const { data: plans } = await supabaseAdmin.from('plans').select('id, name_ar');
                        const matchedPlan = plans?.find(pl =>
                          (planId === 'elite' && (pl.name_ar?.includes('شامل') || pl.id?.includes('elite'))) ||
                          (planId === 'pro'   && (pl.name_ar?.includes('متقدم') || pl.id?.includes('pro'))) ||
                          (planId === 'basic' && (pl.name_ar?.includes('أساسي') || pl.id?.includes('basic')))
                        );
                        const finalPlanId = matchedPlan?.id || planId;

                        // إضافة الدفعة
                        const { data: payRec } = await supabaseAdmin.from('payments').insert({
                          user_id: userData.id, plan_id: finalPlanId,
                          amount_halalas: Math.round(p.amount * 100),
                          moyasar_id: p.id, status: 'paid',
                          paid_at: p.created_at || new Date().toISOString(),
                        }).select().single();

                        // إضافة الاشتراك
                        const exp = new Date(p.created_at || Date.now());
                        exp.setDate(exp.getDate() + 30);
                        await supabaseAdmin.from('subscriptions').upsert({
                          user_id: userData.id, plan_id: finalPlanId,
                          payment_id: payRec?.id || null, status: 'active',
                          start_date: p.created_at || new Date().toISOString(),
                          expires_at: exp.toISOString(),
                        }, { onConflict: 'user_id' });

                        setLinkStatus(prev => ({ ...prev, [p.id]: 'ok' }));
                        await load();
                      }}
                      style={{
                        background: '#7c6df8', color: '#fff', border: 'none', borderRadius: 8,
                        padding: '8px 16px', cursor: 'pointer', fontFamily: 'Tajawal,sans-serif', fontSize: 13, fontWeight: 700,
                        opacity: (!linkPhones[p.id] || st === 'linking') ? 0.5 : 1,
                      }}
                    >{st === 'linking' ? '⏳' : '🔗 ربط'}</button>
                    {st?.startsWith('error') && <span style={{ color: '#f87171', fontSize: 12 }}>{st}</span>}
                    <button
                      type="button"
                      disabled={st === 'linking'}
                      onClick={() => ignorePayment(p)}
                      style={{
                        background: 'transparent', color: '#94a3b8', border: '1px solid rgba(148,163,184,0.4)',
                        borderRadius: 8, padding: '8px 14px', cursor: 'pointer',
                        fontFamily: 'Tajawal,sans-serif', fontSize: 12, fontWeight: 600,
                      }}
                    >🚫 ليست اشتراكاً — تجاهل</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <p style={{ color: '#10b981', fontWeight: 700, marginBottom: 20, fontSize: 18 }}>
        الإجمالي: {total.toFixed(0)} ر.س
      </p>
      <div style={S.card}>
        {loading ? <p style={{ color: 'var(--text-muted)' }}>جاري التحميل...</p> : (
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>التاريخ</th>
                <th style={S.th}>الجوال</th>
                <th style={S.th}>الباقة</th>
                <th style={S.th}>المبلغ</th>
                <th style={S.th}>وسيلة الدفع</th>
                <th style={S.th}>الحالة</th>
              </tr>
            </thead>
            <tbody>
              {payments.map(p => (
                <tr key={p.id}>
                  <td style={S.td}>{new Date(p.paid_at || p.created_at).toLocaleDateString('ar-SA')}</td>
                  <td style={{ ...S.td, direction: 'ltr', fontFamily: 'monospace', fontSize: 12 }}>{p.users?.phone || '—'}</td>
                  <td style={S.td}>{p.plans?.name_ar || '—'}</td>
                  <td style={S.td}>{((p.amount_halalas || 0) / 100).toFixed(0)} ر.س</td>
                  <td style={S.td}>
                    {p.source === 'applepay' ? '🍎 Apple Pay'
                      : p.source === 'stcpay' ? 'STC Pay'
                      : p.source === 'creditcard' ? '💳 ' + (p.card_brand || 'بطاقة')
                      : p.source || '—'}
                  </td>
                  <td style={S.td}>
                    <span style={S.badge(p.status === 'paid' ? '#10b981' : 'var(--text-secondary)')}>
                      {p.status === 'paid' ? '✅ مدفوع' : p.status || '—'}
                    </span>
                  </td>
                </tr>
              ))}
              {!payments.length && (
                <tr><td colSpan={4} style={{ ...S.td, textAlign: 'center', color: 'var(--text-muted)' }}>لا توجد مدفوعات</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─── Questions ───────────────────────────────────────────
function Questions() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const search = useCallback(async () => {
    if (!query.trim()) return;
    setLoading(true); setSearched(true);
    const { data } = await supabase
      .from('questions')
      .select('id, question_ar, options, correct_key, explanation, subject, is_active')
      .ilike('question_ar', `%${query.trim()}%`)
      .limit(30);
    setResults(data || []);
    setLoading(false);
  }, [query]);

  const toggleActive = async (q) => {
    await supabase.from('questions').update({ is_active: !q.is_active }).eq('id', q.id);
    setResults(prev => prev.map(r => r.id === q.id ? { ...r, is_active: !r.is_active } : r));
  };


  const subjectColor = (s) => {
    const map = { كمي:'#3b82f6', لفظي:'#7c6df8', رياضيات:'#10b981', فيزياء:'#f59e0b', كيمياء:'#ef4444', أحياء:'#06b6d4', تاريخ:'#f97316' };
    return map[s] || 'var(--text-secondary)';
  };

  return (
    <div>
      <h2 style={{ marginBottom: 20, color: '#f0b429' }}>🔍 بحث الأسئلة</h2>
      <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
        <input
          style={{ ...S.input }}
          placeholder="ابحث بنص السؤال أو كلمة منه..."
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && search()}
        />
        <button onClick={search} style={{ ...S.btn(), padding: '12px 24px', fontSize: 14, whiteSpace: 'nowrap' }}>
          بحث
        </button>
      </div>

      {loading && <p style={{ color: 'var(--text-muted)' }}>جاري البحث...</p>}

      {searched && !loading && (
        <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 12 }}>
          {results.length} نتيجة
        </p>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {results.map((q, i) => {
          const opts = Array.isArray(q.options) ? q.options : [];
          const isOpen = expanded === q.id;

          return (
            <div key={q.id} style={{ ...S.card, opacity: q.is_active ? 1 : 0.5 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                    <span style={S.badge(subjectColor(q.subject))}>{q.subject || '—'}</span>
                    <span style={S.badge(q.is_active ? '#10b981' : 'var(--text-secondary)')}>
                      {q.is_active ? 'نشط' : 'معطّل'}
                    </span>
                    {/الرسم البياني|الشكل|الصورة|الجدول/.test(q.question_ar) && !q.image_url && (
                      <span style={S.badge('#f97316')}>⚠️ يحتاج صورة — لا يظهر للطلاب</span>
                    )}
                    {/النص|الفقرة|المقطع|القطعة/.test(q.question_ar) && (
                      <span style={S.badge('#ef4444')}>🚫 يحتاج نصاً — لا يظهر للطلاب</span>
                    )}
                  </div>
                  <p style={{ margin: 0, fontSize: 14, lineHeight: 1.7, color: 'var(--text)' }}>
                    {i + 1}. {q.question_ar}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  <button onClick={() => setExpanded(isOpen ? null : q.id)} style={S.btn('var(--border)')}>
                    {isOpen ? 'إخفاء' : 'تفاصيل'}
                  </button>
                  <button onClick={() => toggleActive(q)} style={S.btn(q.is_active ? '#ef4444' : '#10b981')}>
                    {q.is_active ? 'تعطيل' : 'تفعيل'}
                  </button>
                </div>
              </div>

              {isOpen && (
                <div style={{ marginTop: 14, borderTop: '1px solid var(--border)', paddingTop: 14 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
                    {opts.map((o, idx) => (
                      <div key={idx} style={{
                        padding: '8px 12px', borderRadius: 8, fontSize: 13,
                        background: idx === parseInt(q.correct_key) ? 'rgba(16,185,129,0.2)' : 'var(--bg-card-alt)',
                        border: idx === parseInt(q.correct_key) ? '1px solid #10b981' : '1px solid var(--border)',
                        color: idx === parseInt(q.correct_key) ? '#10b981' : '#cbd5e1',
                      }}>
                        {idx === parseInt(q.correct_key) ? '✓ ' : ''}{o}
                      </div>
                    ))}
                  </div>
                  {q.explanation && !q.explanation.startsWith('المصدر') && (
                    <div style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 10, padding: '10px 14px' }}>
                      <span style={{ color: '#f59e0b', fontWeight: 700, fontSize: 13 }}>💡 الشرح: </span>
                      <span style={{ fontSize: 13, color: 'var(--text)' }}>{q.explanation}</span>
                    </div>
                  )}
                  {q.explanation?.startsWith('المصدر') && (
                    <div style={{ background: 'rgba(100,116,139,0.1)', border: '1px solid rgba(100,116,139,0.3)', borderRadius: 10, padding: '10px 14px' }}>
                      <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>💡 لا يوجد شرح لهذا السؤال</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Copy Button ─────────────────────────────────────────
function CopyBtn({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button onClick={copy} style={{
      ...S.btn(copied ? '#10b981' : '#334155'),
      fontSize: 12, border: `1px solid ${copied ? '#10b981' : '#4b5563'}`,
    }}>
      {copied ? '✓ تم النسخ' : '📋 نسخ'}
    </button>
  );
}

// ─── Reports ─────────────────────────────────────────────
const REPORT_LABELS = {
  wrong_answer:   { icon: '❌', label: 'إجابة خاطئة',        color: '#ef4444' },
  not_curriculum: { icon: '📚', label: 'ليس في المنهج',       color: '#3b82f6' },
  wrong_section:  { icon: '📂', label: 'قسم غير مناسب',       color: '#f59e0b' },
  unclear:        { icon: '🌫️', label: 'غير واضح',            color: '#8b5cf6' },
  other:          { icon: '💬', label: 'ملاحظة أخرى',         color: 'var(--text-secondary)' },
  rating_down:    { icon: '👎', label: 'تقييم سلبي',          color: '#f87171' },
  rating_up:      { icon: '👍', label: 'تقييم إيجابي',        color: '#22c55e' },
};

function Reports() {
  const supabase = supabasePublic; // question_reports: RLS معطّل + GRANT للـ anon
  const [reports, setReports]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filter,  setFilter]    = useState('all');
  const [deleting,  setDeleting]  = useState(null);
  const [viewQ,     setViewQ]     = useState(null);
  const [replyId,   setReplyId]   = useState(null); // id البلاغ المفتوح للرد
  const [replyText, setReplyText] = useState('');
  const [replying,  setReplying]  = useState(false);

  const load = async () => {
    setLoading(true);
    // جلب البلاغات
    const { data: reportRows, error } = await supabase
      .from('question_reports')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(300);

    if (error) { console.error('reports error', error); setLoading(false); return; }

    // جلب بيانات الأسئلة المرتبطة
    const qIds = [...new Set((reportRows || []).map(r => r.question_id).filter(Boolean))];
    let questionsMap = {};
    if (qIds.length > 0) {
      const { data: qRows } = await supabase
        .from('questions')
        .select('id, question_ar, subject, options, correct_key, explanation')
        .in('id', qIds);
      (qRows || []).forEach(q => { questionsMap[q.id] = q; });
    }

    const merged = (reportRows || []).map(r => ({
      ...r,
      questions: questionsMap[r.question_id] || null,
    }));
    setReports(merged);
    setLoading(false);
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const deleteQuestion = async (questionId, reportId) => {
    if (!window.confirm('حذف السؤال نهائياً؟')) return;
    setDeleting(reportId);
    await supabase.from('questions').update({ is_active: false }).eq('id', questionId);
    await supabase.from('question_reports').delete().eq('id', reportId);
    setReports(prev => prev.filter(r => r.id !== reportId));
    setDeleting(null);
  };

  const dismissReport = async (reportId) => {
    await supabase.from('question_reports').delete().eq('id', reportId);
    setReports(prev => prev.filter(r => r.id !== reportId));
  };

  const sendReply = async () => {
    if (!replyText.trim() || !replyId) return;
    setReplying(true);
    await supabase.from('question_reports').update({
      admin_reply: replyText.trim(),
      replied_at:  new Date().toISOString(),
    }).eq('id', replyId);
    setReports(prev => prev.map(r => r.id === replyId
      ? { ...r, admin_reply: replyText.trim(), replied_at: new Date().toISOString() }
      : r
    ));
    setReplyId(null);
    setReplyText('');
    setReplying(false);
  };

  const filtered = filter === 'all' ? reports : reports.filter(r => r.report_type === filter);
  const counts   = reports.reduce((acc, r) => { acc[r.report_type] = (acc[r.report_type] || 0) + 1; return acc; }, {});

  return (
    <div>

      {/* ── popup عرض السؤال ── */}
      {viewQ && (
        <div onClick={() => setViewQ(null)} style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: '#0d1117', border: '1px solid #334155',
            borderRadius: 20, padding: '24px 20px',
            width: '100%', maxWidth: 520,
            direction: 'rtl', fontFamily: 'Tajawal,sans-serif',
            maxHeight: '85vh', overflowY: 'auto',
          }}>
            {/* رأس */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <span style={{ ...S.badge('var(--border-header)'), fontSize: 12 }}>{viewQ.subject || 'عام'}</span>
              <button onClick={() => setViewQ(null)} style={{
                background: 'none', border: 'none', color: 'var(--text-muted)',
                fontSize: 22, cursor: 'pointer', lineHeight: 1,
              }}>✕</button>
            </div>

            {/* نص السؤال */}
            <p style={{
              color: 'var(--text)', fontSize: 16, fontWeight: 700,
              lineHeight: 1.8, margin: '0 0 18px',
            }}>{viewQ.question_ar}</p>

            {/* الخيارات */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
              {(() => {
                let opts = [];
                try { opts = Array.isArray(viewQ.options) ? viewQ.options : JSON.parse(viewQ.options || '[]'); } catch {}
                const correct = Number(viewQ.correct_key);
                return opts.map((opt, i) => {
                  const isCorrect = i === correct;
                  return (
                    <div key={i} style={{
                      background: isCorrect ? 'rgba(34,197,94,0.1)' : '#0d1117',
                      border: `1.5px solid ${isCorrect ? '#22c55e' : '#334155'}`,
                      borderRadius: 10, padding: '10px 14px',
                      display: 'flex', alignItems: 'center', gap: 10,
                    }}>
                      <span style={{
                        background: isCorrect ? 'rgba(34,197,94,0.2)' : 'var(--bg-input)',
                        borderRadius: 6, padding: '2px 9px',
                        color: isCorrect ? '#86efac' : 'var(--text-muted)', fontSize: 12,
                      }}>
                        {['أ','ب','ج','د'][i]}
                      </span>
                      <span style={{ color: isCorrect ? '#86efac' : 'var(--text-secondary)', fontSize: 14, flex: 1 }}>
                        {opt}
                      </span>
                      {isCorrect && <span style={{ color: '#86efac', fontSize: 13, fontWeight: 900 }}>✓ صحيح</span>}
                    </div>
                  );
                });
              })()}
            </div>

            {/* الشرح */}
            {viewQ.explanation && !viewQ.explanation.includes('المصدر') && (
              <div style={{
                background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.25)',
                borderRadius: 10, padding: '10px 14px',
              }}>
                <p style={{ color: '#fbbf24', fontSize: 13, margin: 0 }}>💡 {viewQ.explanation}</p>
              </div>
            )}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, color: '#f0b429' }}>🚩 بلاغات الطلاب</h2>
        <div style={{
          background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)',
          borderRadius: 10, padding: '4px 14px',
          color: '#fca5a5', fontWeight: 900, fontSize: 18,
        }}>{reports.length}</div>
      </div>

      {/* فلتر النوع */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
        <button onClick={() => setFilter('all')} style={{
          ...S.btn(filter === 'all' ? '#7c3aed' : 'var(--bg-input)'),
          fontSize: 12,
        }}>الكل ({reports.length})</button>
        {Object.entries(REPORT_LABELS).map(([id, meta]) => (
          counts[id] ? (
            <button key={id} onClick={() => setFilter(id)} style={{
              ...S.btn(filter === id ? meta.color : 'var(--bg-card)'),
              fontSize: 12, border: `1px solid ${filter === id ? meta.color : 'transparent'}`,
            }}>
              {meta.icon} {meta.label} ({counts[id] || 0})
            </button>
          ) : null
        ))}
      </div>

      {loading ? (
        <p style={{ color: 'var(--text-muted)' }}>جاري التحميل...</p>
      ) : filtered.length === 0 ? (
        <div style={{ ...S.card, textAlign: 'center', padding: 40 }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
          <p style={{ color: 'var(--text-muted)' }}>لا توجد بلاغات</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map(r => {
            const meta = REPORT_LABELS[r.report_type] || REPORT_LABELS.other;
            return (
              <div key={r.id} style={{ ...S.card, borderRight: `4px solid ${meta.color}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ ...S.badge(meta.color), fontSize: 12 }}>
                      {meta.icon} {meta.label}
                    </span>
                    {(r.user_phone || r.phone) ? (
                      <span style={{
                        background: 'rgba(124,109,248,0.15)', border: '1px solid rgba(124,109,248,0.35)',
                        borderRadius: 20, padding: '2px 10px',
                        color: '#a78bfa', fontSize: 12, fontWeight: 700,
                        direction: 'ltr', letterSpacing: 0.5,
                      }}>
                        📱 {(r.user_phone || r.phone).replace(/^\+966/, '0')}
                      </span>
                    ) : (
                      <span style={{
                        background: 'rgba(100,116,139,0.15)', border: '1px solid rgba(100,116,139,0.3)',
                        borderRadius: 20, padding: '2px 10px',
                        color: '#94a3b8', fontSize: 12, fontWeight: 600,
                      }}>
                        👤 زائر
                      </span>
                    )}
                  </div>
                  <span style={{
                    color: '#94a3b8', fontSize: 12, fontWeight: 600,
                    background: 'rgba(148,163,184,0.1)', borderRadius: 8,
                    padding: '2px 8px', border: '1px solid rgba(148,163,184,0.2)',
                  }}>
                    {new Date(r.created_at).toLocaleDateString('ar-SA', { year: 'numeric', month: 'short', day: 'numeric' })}
                  </span>
                </div>

                {/* نص السؤال */}
                <p style={{
                  color: 'var(--text)', fontSize: 13, margin: '0 0 6px',
                  lineHeight: 1.6, direction: 'rtl',
                  display: '-webkit-box', WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical', overflow: 'hidden',
                }}>
                  {r.questions?.question_ar || '—'}
                </p>

                <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ ...S.badge('var(--border-header)'), fontSize: 11 }}>
                    {r.questions?.subject || 'عام'}
                  </span>
                  {r.note && (
                    <span style={{ color: '#fbbf24', fontSize: 12, fontStyle: 'italic' }}>
                      "{r.note}"
                    </span>
                  )}
                </div>

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {r.questions && (
                    <button
                      onClick={() => setViewQ(r.questions)}
                      style={{ ...S.btn('#1e40af'), fontSize: 12, border: '1px solid #3b82f6' }}
                    >
                      👁️ عرض السؤال
                    </button>
                  )}
                  {r.questions?.question_ar && (
                    <CopyBtn text={r.questions.question_ar} />
                  )}
                  <button
                    onClick={() => { setReplyId(r.id); setReplyText(r.admin_reply || ''); }}
                    style={{ ...S.btn('#065f46'), fontSize: 12, border: '1px solid #10b981' }}
                  >
                    💬 {r.admin_reply ? 'تعديل الرد' : 'رد على الطالب'}
                  </button>
                  <button
                    onClick={() => deleteQuestion(r.questions?.id, r.id)}
                    disabled={deleting === r.id}
                    style={{ ...S.btn('#ef4444'), fontSize: 12 }}
                  >
                    {deleting === r.id ? '...' : '🗑️ حذف السؤال'}
                  </button>
                  <button
                    onClick={() => dismissReport(r.id)}
                    style={{ ...S.btn('var(--bg-input)'), fontSize: 12 }}
                  >
                    ✓ تجاهل البلاغ
                  </button>
                </div>

                {/* رد الأدمن الموجود */}
                {r.admin_reply && replyId !== r.id && (
                  <div style={{
                    marginTop: 10, padding: '8px 12px',
                    background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.25)',
                    borderRadius: 8,
                  }}>
                    <span style={{ color: '#10b981', fontSize: 11, fontWeight: 700 }}>✅ رد الأدمن: </span>
                    <span style={{ color: '#cbd5e1', fontSize: 12 }}>{r.admin_reply}</span>
                  </div>
                )}

                {/* صندوق الرد */}
                {replyId === r.id && (
                  <div style={{ marginTop: 10 }}>
                    <textarea
                      value={replyText}
                      onChange={e => setReplyText(e.target.value)}
                      placeholder="اكتب ردك على الطالب..."
                      rows={3}
                      maxLength={400}
                      style={{
                        width: '100%', boxSizing: 'border-box',
                        background: '#0d1321', border: '1px solid #10b981',
                        borderRadius: 8, padding: '10px 12px',
                        color: '#fff', fontSize: 13,
                        fontFamily: 'Tajawal,sans-serif',
                        resize: 'none', outline: 'none', direction: 'rtl',
                        marginBottom: 8,
                      }}
                    />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        onClick={sendReply}
                        disabled={!replyText.trim() || replying}
                        style={{ ...S.btn('#10b981'), fontSize: 12 }}
                      >
                        {replying ? '⏳ جاري...' : '✅ إرسال الرد'}
                      </button>
                      <button
                        onClick={() => { setReplyId(null); setReplyText(''); }}
                        style={{ ...S.btn('var(--bg-input)'), fontSize: 12 }}
                      >
                        إلغاء
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Reviews ─────────────────────────────────────────────
function Reviews() {
  const [all,     setAll]     = useState([]);   // كل الآراء بلا فلترة
  const [loading, setLoading] = useState(true);
  const [filter,  setFilter]  = useState('all'); // all | pending | approved | 5 | 4 | 3 | 2 | 1
  const [search,  setSearch]  = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('reviews')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(200);
    setAll(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    await supabase.from('reviews').update({ approved: true }).eq('id', id);
    setAll(prev => prev.map(r => r.id === id ? { ...r, approved: true } : r));
  };

  const reject = async (id) => {
    if (!window.confirm('حذف هذا الرأي نهائياً؟')) return;
    await supabase.from('reviews').delete().eq('id', id);
    setAll(prev => prev.filter(r => r.id !== id));
  };

  // إحصاء
  const counts = {
    all:      all.length,
    pending:  all.filter(r => !r.approved).length,
    approved: all.filter(r =>  r.approved).length,
    5: all.filter(r => r.stars === 5).length,
    4: all.filter(r => r.stars === 4).length,
    3: all.filter(r => r.stars === 3).length,
    2: all.filter(r => r.stars === 2).length,
    1: all.filter(r => r.stars === 1).length,
  };

  // فلترة
  const base = all
    .filter(r => {
      if (filter === 'pending')  return !r.approved;
      if (filter === 'approved') return  r.approved;
      if (['5','4','3','2','1'].includes(filter)) return r.stars === Number(filter);
      return true;
    })
    .filter(r => {
      if (!search.trim()) return true;
      const q = search.trim().toLowerCase();
      return (
        (r.reviewer_name  || '').toLowerCase().includes(q) ||
        (r.display_name   || '').toLowerCase().includes(q) ||
        (r.comment        || '').toLowerCase().includes(q) ||
        (r.stage          || '').toLowerCase().includes(q)
      );
    });

  const FILTERS = [
    { id: 'all',      label: 'الكل',               color: '#7c3aed' },
    { id: 'pending',  label: '⏳ انتظار',           color: '#f59e0b' },
    { id: 'approved', label: '✅ معتمدة',           color: '#10b981' },
    { id: '5',        label: '★★★★★',              color: '#f59e0b' },
    { id: '4',        label: '★★★★',               color: '#f59e0b' },
    { id: '3',        label: '★★★',                color: '#f97316' },
    { id: '2',        label: '★★',                 color: '#ef4444' },
    { id: '1',        label: '★',                  color: '#ef4444' },
  ];

  return (
    <div>
      {/* ─── رأس الصفحة ─── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <h2 style={{ margin: 0, color: '#f0b429' }}>⭐ آراء المستخدمين</h2>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <div style={{
            background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.3)',
            borderRadius: 10, padding: '4px 14px', color: '#fbbf24', fontWeight: 900, fontSize: 18,
          }}>{counts.all}</div>
          <div style={{
            background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)',
            borderRadius: 10, padding: '4px 14px', color: '#fca5a5', fontWeight: 700, fontSize: 14,
          }}>⏳ {counts.pending} بانتظار</div>
        </div>
      </div>

      {/* ─── بحث ─── */}
      <input
        style={{ ...S.input, marginBottom: 14 }}
        placeholder="🔍 ابحث بالاسم أو التعليق أو المرحلة..."
        value={search}
        onChange={e => setSearch(e.target.value)}
      />

      {/* ─── فلاتر ─── */}
      <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 20 }}>
        {FILTERS.map(f => counts[f.id] > 0 || f.id === 'all' ? (
          <button key={f.id} onClick={() => setFilter(f.id)} style={{
            ...S.btn(filter === f.id ? f.color : 'var(--bg-card-alt)'),
            fontSize: 12,
            border: `1px solid ${filter === f.id ? f.color : 'rgba(255,255,255,0.1)'}`,
          }}>
            {f.label} ({counts[f.id] || 0})
          </button>
        ) : null)}
      </div>

      {loading ? <p style={{ color: 'var(--text-muted)' }}>جاري التحميل...</p> : (
        <>
          <p style={{ color: 'var(--text-muted)', fontSize: 12, marginBottom: 12 }}>
            يُعرض {base.length} من {counts.all} رأي
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {base.map(r => (
              <div key={r.id} style={{
                ...S.card,
                borderRight: `4px solid ${r.approved ? '#10b981' : '#f59e0b'}`,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>

                    {/* صف المعلومات */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                      {/* حالة */}
                      <span style={{
                        ...S.badge(r.approved ? '#10b981' : '#f59e0b'),
                        fontSize: 10,
                      }}>
                        {r.approved ? '✅ معتمد' : '⏳ انتظار'}
                      </span>

                      {/* الاسم */}
                      <span style={{ fontWeight: 700, color: 'var(--text)', fontSize: 14 }}>
                        {r.reviewer_name || r.display_name || 'مستخدم'}
                      </span>

                      {/* المرحلة */}
                      {r.stage && (
                        <span style={{
                          background: 'rgba(124,109,248,0.2)', border: '1px solid rgba(124,109,248,0.4)',
                          borderRadius: 20, padding: '2px 10px',
                          color: '#a78bfa', fontSize: 11, fontWeight: 700,
                        }}>{r.stage}</span>
                      )}

                      {/* النجوم */}
                      <span style={{ color: '#f59e0b', fontSize: 13, letterSpacing: 1 }}>
                        {'★'.repeat(r.stars)}{'☆'.repeat(5 - r.stars)}
                      </span>

                      {/* التاريخ */}
                      <span style={{ fontSize: 11, color: 'var(--text-muted)', marginRight: 'auto' }}>
                        {new Date(r.created_at).toLocaleDateString('ar-SA')}
                      </span>
                    </div>

                    {/* التعليق */}
                    <p style={{ margin: 0, fontSize: 13.5, color: '#cbd5e1', lineHeight: 1.75 }}>
                      {r.comment || <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>لا يوجد تعليق</span>}
                    </p>
                  </div>

                  {/* أزرار */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 7, flexShrink: 0 }}>
                    {!r.approved && (
                      <button onClick={() => approve(r.id)} style={S.btn('#10b981')}>✓ قبول</button>
                    )}
                    <button onClick={() => reject(r.id)} style={S.btn('#ef4444')}>✕ حذف</button>
                  </div>
                </div>
              </div>
            ))}

            {!base.length && (
              <div style={{ ...S.card, textAlign: 'center', padding: 40 }}>
                <div style={{ fontSize: 36, marginBottom: 10 }}>🔍</div>
                <p style={{ color: 'var(--text-muted)', margin: 0 }}>لا توجد نتائج</p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Delete Button ───────────────────────────────────────
function DeleteButton({ label, color, ids, isDelete, onDone }) {
  const [loading, setLoading] = useState(false);
  const [done, setDone]       = useState(false);

  const handle = async () => {
    if (!ids.length) return;
    const confirm = window.confirm(
      `${isDelete ? 'حذف' : 'تعطيل'} ${ids.length} سؤال؟\nهذا الإجراء ${isDelete ? 'لا يمكن التراجع عنه!' : 'قابل للتراجع.'}`
    );
    if (!confirm) return;
    setLoading(true);

    // نعالج على دفعات 100
    const chunks = [];
    for (let i = 0; i < ids.length; i += 100) chunks.push(ids.slice(i, i + 100));

    for (const chunk of chunks) {
      if (isDelete) {
        await supabase.from('questions').delete().in('id', chunk);
      } else {
        await supabase.from('questions').update({ is_active: false }).in('id', chunk);
      }
    }

    setLoading(false);
    setDone(true);
    onDone();
  };

  return (
    <button onClick={handle} disabled={loading || done || !ids.length} style={{
      ...S.btn(done ? '#10b981' : ids.length ? color : 'var(--border)'),
      padding: '10px 18px', fontSize: 13, opacity: (!ids.length || done) ? 0.6 : 1,
    }}>
      {done ? '✅ تم' : loading ? '⏳ جاري...' : `${label} (${ids.length})`}
    </button>
  );
}

// ─── Hidden Questions ────────────────────────────────────
function HiddenQuestions() {
  const [stats, setStats]       = useState(null);
  const [list, setList]         = useState([]);
  const [aiList, setAiList]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState('all');
  const [section, setSection]   = useState('auto'); // auto | ai
  const [aiVisible, setAiVisible] = useState(50);

  useEffect(() => {
    const load = async () => {
      setLoading(true);

      // ── جلب الأسئلة النشطة (مخفية تلقائياً) ──
      let allData = [];
      let from = 0;
      const pageSize = 1000;
      while (true) {
        const { data } = await supabase
          .from('questions')
          .select('id, question_ar, options, subject, image_url, is_active')
          .eq('is_active', true)
          .range(from, from + pageSize - 1);
        if (!data?.length) break;
        allData = [...allData, ...data];
        if (data.length < pageSize) break;
        from += pageSize;
      }

      const needsPassage  = (t) => /النص|الفقرة|المقطع|القطعة/.test(t || '');
      const needsVisual   = (t) => /الرسم البياني|الشكل|الصورة|الجدول/.test(t || '');
      const hasDotOptions = (q) => {
        const opts = Array.isArray(q.options) ? q.options : [];
        return opts.some(o => /^[.\s،,…]+$/.test(String(o || '').trim()));
      };
      const hasEmptyOptions = (q) => {
        const opts = Array.isArray(q.options) ? q.options : [];
        return opts.length < 4 || opts.some(o => !String(o || '').trim());
      };

      const hidden = allData.map(q => {
        const reasons = [];
        if (needsPassage(q.question_ar))                 reasons.push('يحتاج نصاً مقروءاً');
        if (needsVisual(q.question_ar) && !q.image_url) reasons.push('يحتاج صورة/رسم غير موجود');
        if (hasDotOptions(q))                            reasons.push('خيارات ناقصة ......');
        if (hasEmptyOptions(q))                          reasons.push('أقل من 4 خيارات');
        return { ...q, reasons };
      }).filter(q => q.reasons.length > 0);

      const byReason = {
        'يحتاج نصاً مقروءاً':       hidden.filter(q => q.reasons.includes('يحتاج نصاً مقروءاً')).length,
        'يحتاج صورة/رسم غير موجود': hidden.filter(q => q.reasons.includes('يحتاج صورة/رسم غير موجود')).length,
        'خيارات ناقصة ......':       hidden.filter(q => q.reasons.includes('خيارات ناقصة ......')).length,
        'أقل من 4 خيارات':           hidden.filter(q => q.reasons.includes('أقل من 4 خيارات')).length,
      };

      setStats({ total: hidden.length, allCount: allData.length, byReason });
      setList(hidden);

      // ── جلب أسئلة AI (is_active=false & flag_reason=ai_error) ──
      let aiData = [];
      let aiFrom = 0;
      while (true) {
        const { data } = await supabase
          .from('questions')
          .select('id, question_ar, options, correct_key, suggested_key, subject, flag_reason')
          .eq('is_active', false)
          .eq('flag_reason', 'ai_error')
          .range(aiFrom, aiFrom + pageSize - 1);
        if (!data?.length) break;
        aiData = [...aiData, ...data];
        if (data.length < pageSize) break;
        aiFrom += pageSize;
      }
      setAiList(aiData);
      setLoading(false);
    };
    load();
  }, []);

  const filtered = filter === 'all' ? list : list.filter(q => q.reasons.includes(filter));

  const reasonColor = (r) => {
    if (r.includes('نصاً'))  return '#ef4444';
    if (r.includes('صورة'))  return '#f97316';
    if (r.includes('ناقصة')) return '#7c6df8';
    return 'var(--text-secondary)';
  };

  const restoreQuestion = async (id) => {
    await supabase.from('questions').update({ is_active: true, flag_reason: null }).eq('id', id);
    setAiList(prev => prev.filter(q => q.id !== id));
  };

  const fixQuestion = async (id, suggestedKey) => {
    await supabase.from('questions').update({
      is_active: true,
      correct_key: suggestedKey,
      flag_reason: null,
      suggested_key: null,
    }).eq('id', id);
    setAiList(prev => prev.filter(q => q.id !== id));
  };

  const deleteQuestion = async (id) => {
    if (!window.confirm('حذف هذا السؤال نهائياً؟')) return;
    await supabase.from('questions').delete().eq('id', id);
    setAiList(prev => prev.filter(q => q.id !== id));
  };

  return (
    <div>
      <h2 style={{ marginBottom: 16, color: '#f0b429' }}>🚫 الأسئلة المخفية عن الطلاب</h2>

      {/* ── تبويبات القسم ── */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <button onClick={() => setSection('auto')} style={S.tab(section === 'auto')}>
          ⚙️ مخفية تلقائياً {stats ? `(${stats.total})` : ''}
        </button>
        <button onClick={() => setSection('ai')} style={S.tab(section === 'ai')}>
          🤖 اكتشافات الذكاء الاصطناعي {aiList.length ? `(${aiList.length})` : ''}
        </button>
      </div>

      {loading ? <p style={{ color: 'var(--text-muted)' }}>جاري التحليل... قد يأخذ لحظة</p> : (
        <>
          {/* ══ قسم المخفية تلقائياً ══ */}
          {section === 'auto' && (
            <>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
                <div style={{ ...S.statCard, borderTop: '3px solid #ef4444', cursor: 'pointer' }}
                  onClick={() => setFilter('all')}>
                  <div style={{ fontSize: 28, fontWeight: 800, color: '#ef4444' }}>{stats.total.toLocaleString()}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>إجمالي المخفية</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                    من أصل {stats.allCount.toLocaleString()} سؤال
                  </div>
                </div>
                {Object.entries(stats.byReason).map(([reason, count]) => (
                  <div key={reason}
                    style={{ ...S.statCard, borderTop: `3px solid ${reasonColor(reason)}`, cursor: 'pointer',
                      outline: filter === reason ? `2px solid ${reasonColor(reason)}` : 'none' }}
                    onClick={() => setFilter(filter === reason ? 'all' : reason)}>
                    <div style={{ fontSize: 24, fontWeight: 800, color: reasonColor(reason) }}>{count.toLocaleString()}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>{reason}</div>
                  </div>
                ))}
              </div>

              <div style={{ ...S.card, marginBottom: 20, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)' }}>
                <p style={{ margin: '0 0 16px', color: '#fbbf24', fontSize: 13, lineHeight: 2 }}>
                  💡 <strong>كيف تعالجها:</strong><br/>
                  🔴 <strong>يحتاج نصاً:</strong> احذفها — لا يمكن إصلاحها بدون النص الأصلي<br/>
                  🟠 <strong>يحتاج صورة:</strong> استخرج صورها من PDF وارفعها على Supabase<br/>
                  🟣 <strong>خيارات ناقصة:</strong> راجع PDF واستكمل الخيارات أو احذفها
                </p>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {[
                    { reason: 'يحتاج نصاً مقروءاً',        label: '🔴 حذف أسئلة النص',         color: '#ef4444' },
                    { reason: 'يحتاج صورة/رسم غير موجود',  label: '🟠 تعطيل أسئلة الصور',       color: '#f97316' },
                    { reason: 'خيارات ناقصة ......',        label: '🟣 تعطيل الخيارات الناقصة',  color: '#7c6df8' },
                  ].map(({ reason, label, color }) => (
                    <DeleteButton key={reason} label={label} color={color}
                      ids={list.filter(q => q.reasons.includes(reason)).map(q => q.id)}
                      isDelete={reason === 'يحتاج نصاً مقروءاً'}
                      onDone={() => setList(prev => prev.filter(q => !q.reasons.includes(reason)))}
                    />
                  ))}
                </div>
              </div>

              <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 12 }}>
                عرض: {filtered.length} سؤال
                {filter !== 'all' && <span style={{ color: reasonColor(filter) }}> — {filter}</span>}
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {filtered.slice(0, 50).map((q, i) => (
                  <div key={q.id} style={{ ...S.card, padding: '12px 16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                      <p style={{ margin: 0, fontSize: 13, color: '#cbd5e1', flex: 1, lineHeight: 1.6 }}>
                        {i + 1}. {q.question_ar?.slice(0, 120)}{q.question_ar?.length > 120 ? '...' : ''}
                      </p>
                      <div style={{ display: 'flex', gap: 6, flexShrink: 0, flexWrap: 'wrap' }}>
                        {q.reasons.map(r => (
                          <span key={r} style={S.badge(reasonColor(r))}>{r}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
                {filtered.length > 50 && (
                  <p style={{ color: 'var(--text-muted)', textAlign: 'center', fontSize: 13 }}>
                    ... و {filtered.length - 50} سؤال إضافي
                  </p>
                )}
              </div>
            </>
          )}

          {/* ══ قسم اكتشافات AI ══ */}
          {section === 'ai' && (
            <>
              {/* بطاقة إحصاء */}
              <div style={{ ...S.card, marginBottom: 20, background: 'rgba(124,109,248,0.08)', border: '1px solid rgba(124,109,248,0.25)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 36 }}>🤖</div>
                  <div>
                    <div style={{ color: '#a78bfa', fontWeight: 800, fontSize: 22 }}>{aiList.length.toLocaleString()} سؤال</div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                      تم اكتشاف خطأ في الإجابة بواسطة الذكاء الاصطناعي وتعطيلها تلقائياً
                    </div>
                  </div>
                  <div style={{ marginRight: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {/* زر تطبيق الكل */}
                    {(() => {
                      const fixable = aiList.filter(q => {
                        const sk = q.suggested_key !== null && q.suggested_key !== undefined ? parseInt(q.suggested_key) : null;
                        const ck = parseInt(q.correct_key);
                        const opts = Array.isArray(q.options) ? q.options : [];
                        return sk !== null && sk !== ck && opts[sk];
                      });
                      return fixable.length > 0 ? (
                        <button
                          onClick={async () => {
                            if (!window.confirm(`تطبيق الإجابة الصحيحة على ${fixable.length} سؤال وإعادتها للطلاب؟`)) return;
                            for (let i = 0; i < fixable.length; i += 50) {
                              const chunk = fixable.slice(i, i + 50);
                              for (const q of chunk) {
                                await supabase.from('questions').update({
                                  is_active: true,
                                  correct_key: parseInt(q.suggested_key),
                                  flag_reason: null,
                                  suggested_key: null,
                                }).eq('id', q.id);
                              }
                            }
                            setAiList(prev => prev.filter(q => !fixable.find(f => f.id === q.id)));
                          }}
                          style={{ ...S.btn('#10b981'), padding: '8px 16px' }}
                        >
                          ✅ تطبيق الكل ({fixable.length})
                        </button>
                      ) : null;
                    })()}
                    <button
                      onClick={async () => {
                        if (!window.confirm(`حذف كل ${aiList.length} سؤال نهائياً؟`)) return;
                        const ids = aiList.map(q => q.id);
                        for (let i = 0; i < ids.length; i += 100) {
                          await supabase.from('questions').delete().in('id', ids.slice(i, i + 100));
                        }
                        setAiList([]);
                      }}
                      style={{ ...S.btn('#ef4444'), padding: '8px 16px' }}
                    >
                      🗑️ حذف الكل
                    </button>
                  </div>
                </div>
              </div>

              {/* قائمة */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {aiList.slice(0, aiVisible).map((q, i) => {
                  const opts = Array.isArray(q.options) ? q.options : [];
                  const ck   = parseInt(q.correct_key);
                  const sk   = q.suggested_key !== null && q.suggested_key !== undefined ? parseInt(q.suggested_key) : null;
                  const letters = ['أ','ب','ج','د','هـ'];
                  const hasFix  = sk !== null && sk !== ck && opts[sk];
                  return (
                    <div key={q.id} style={{ ...S.card, borderRight: `4px solid ${hasFix ? '#10b981' : '#7c6df8'}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                            <span style={S.badge('#7c6df8')}>{q.subject || 'عام'}</span>
                            <span style={S.badge('#1e3a5f')}>🤖 اكتشاف AI</span>
                            {hasFix && <span style={S.badge('#10b981')}>✅ يوجد اقتراح</span>}
                          </div>
                          <p style={{ margin: '0 0 8px', fontSize: 13.5, color: 'var(--text)', lineHeight: 1.7 }}>
                            {i + 1}. {q.question_ar}
                          </p>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
                            {opts[ck] && (
                              <div style={{
                                display: 'inline-flex', alignItems: 'center', gap: 6,
                                background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
                                borderRadius: 8, padding: '4px 10px', fontSize: 12,
                              }}>
                                <span style={{ color: '#fca5a5' }}>❌ الخاطئة:</span>
                                <span style={{ color: '#ef4444', fontWeight: 700 }}>
                                  {letters[ck] || ck}) {opts[ck]}
                                </span>
                              </div>
                            )}
                            {hasFix && (
                              <div style={{
                                display: 'inline-flex', alignItems: 'center', gap: 6,
                                background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)',
                                borderRadius: 8, padding: '4px 10px', fontSize: 12,
                              }}>
                                <span style={{ color: '#6ee7b7' }}>✅ الصحيحة:</span>
                                <span style={{ color: '#10b981', fontWeight: 700 }}>
                                  {letters[sk] || sk}) {opts[sk]}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
                          {hasFix && (
                            <button onClick={() => fixQuestion(q.id, sk)} style={S.btn('#10b981')}>
                              ✅ تطبيق الإجابة
                            </button>
                          )}
                          <button onClick={() => restoreQuestion(q.id)} style={S.btn('var(--text-secondary)')}>↩️ استعادة</button>
                          <button onClick={() => deleteQuestion(q.id)}  style={S.btn('#ef4444')}>🗑️ حذف</button>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {aiVisible < aiList.length && (
                  <div style={{ textAlign: 'center', padding: '16px 0' }}>
                    <button
                      onClick={() => setAiVisible(v => v + 50)}
                      style={{ ...S.btn('#7c6df8'), padding: '10px 28px', fontSize: 14 }}
                    >
                      ⬇️ تحميل 50 إضافية ({aiList.length - aiVisible} متبقية)
                    </button>
                  </div>
                )}
                {!aiList.length && (
                  <div style={{ ...S.card, textAlign: 'center', padding: 40 }}>
                    <div style={{ fontSize: 40, marginBottom: 10 }}>🎉</div>
                    <p style={{ color: 'var(--text-muted)', margin: 0 }}>لا توجد أسئلة مُعلَّمة بواسطة AI</p>
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

// ─── Telegram Bot ─────────────────────────────────────────
function TelegramBot() {
  const [totalCount, setTotalCount] = useState(0);
  const [linkedCount, setLinkedCount] = useState(0);
  const [message, setMessage] = useState('');
  const [linkedOnly, setLinkedOnly] = useState(true);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    const { count: total } = await supabase.from('telegram_users').select('*', { count: 'exact', head: true });
    const { count: linked } = await supabase.from('telegram_users').select('*', { count: 'exact', head: true }).not('user_id', 'is', null);
    setTotalCount(total || 0);
    setLinkedCount(linked || 0);
  }, []);

  useEffect(() => { load(); }, [load]);

  const broadcast = async () => {
    if (!message.trim()) return;
    setSending(true);
    setResult(null);
    try {
      const { data: { session } } = await supabasePublic.auth.getSession();
      if (!session) {
        setResult({ ok: false, error: 'الجلسة منتهية — سجّل دخول من جديد' });
        setSending(false);
        return;
      }

      const res = await fetch('/api/telegram-broadcast', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ message, linkedOnly }),
      });
      const data = await res.json();
      setResult(data);
      if (data.ok) setMessage('');
    } catch (e) {
      setResult({ ok: false, error: e.message });
    }
    setSending(false);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <h2 style={{ margin: 0, color: '#f0b429' }}>🤖 بوت تيليقرام</h2>
      </div>

      <div style={{ display: 'flex', gap: 14, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={S.statCard}>
          <div style={{ fontSize: 26, fontWeight: 900, color: '#a855f7' }}>{totalCount}</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>إجمالي مستخدمي البوت</div>
        </div>
        <div style={S.statCard}>
          <div style={{ fontSize: 26, fontWeight: 900, color: '#10b981' }}>{linkedCount}</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>حسابات مرتبطة بقادر</div>
        </div>
      </div>

      <div style={S.card}>
        <h3 style={{ marginTop: 0, fontSize: 15 }}>📢 إرسال بث لمستخدمي البوت</h3>
        <textarea
          style={{ ...S.input, minHeight: 100, resize: 'vertical', fontFamily: 'Tajawal,sans-serif' }}
          placeholder="اكتب رسالة البث هنا..."
          value={message}
          onChange={e => setMessage(e.target.value)}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '12px 0', fontSize: 13, color: 'var(--text-secondary)' }}>
          <input type="checkbox" checked={linkedOnly} onChange={e => setLinkedOnly(e.target.checked)} />
          إرسال للحسابات المرتبطة فقط
        </label>
        <button style={S.btn('#7c3aed')} onClick={broadcast} disabled={sending || !message.trim()}>
          {sending ? 'جارٍ الإرسال...' : 'إرسال البث'}
        </button>
        {result && (
          <p style={{ marginTop: 12, fontSize: 13, color: result.ok ? '#86efac' : '#fca5a5' }}>
            {result.ok
              ? `تم الإرسال: ${result.sent} نجح، ${result.failed} فشل من أصل ${result.total}`
              : `خطأ: ${result.error}`}
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Main AdminScreen ─────────────────────────────────────
export default function AdminScreen({ user, onLogout, onHome, theme, setTheme, restrictToQuestions }) {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState(restrictToQuestions ? 'questions' : 'dashboard');
  const visibleTabs = restrictToQuestions ? TABS.filter(tab => tab.id === 'questions') : TABS;

  const renderTab = () => {
    switch (activeTab) {
      case 'dashboard':  return <Dashboard adminPhone={user?.phone} />;
      case 'analytics':  return <Analytics />;
      case 'users':      return <Users />;
      case 'payments':   return <Payments />;
      case 'questions':  return <Questions />;
      case 'hidden':     return <HiddenQuestions />;
      case 'reports':    return <Reports />;
      case 'reviews':    return <Reviews />;
      case 'telegram':   return <TelegramBot />;
      default:           return <Dashboard adminPhone={user?.phone} />;
    }
  };

  return (
    <div style={S.wrap}>
      {/* Header */}
      <div style={S.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={onHome} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <LogoIcon size={28} />
            <span style={{ color: '#f0b429', fontWeight: 800, fontSize: 18, fontFamily: 'Tajawal,sans-serif' }}>{t('appName')}</span>
          </button>
          <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>|</span>
          <span style={{ color: '#a855f7', fontSize: 13, fontWeight: 600 }}>{t('admin_title')}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{user?.phone}</span>
          <button onClick={onLogout} style={S.btn('var(--border)')}>{t('logout')}</button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, padding: '16px 24px', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
        {visibleTabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={S.tab(activeTab === tab.id)}>
            {tab.icon} {t(tab.labelKey)}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ padding: '24px', maxWidth: 1100, margin: '0 auto' }}>
        {renderTab()}
      </div>
    </div>
  );
}
